<template>
	<div class="subscription-container">
		<!-- Error display (shown at top but doesn't hide content) -->
		<section v-if="error" class="error-section">
			<div class="error-container">
				<p class="error-title">Something went wrong</p>
				<p class="error-message">{{ error }}</p>
			</div>
		</section>

		<!-- Loading State -->
		<section v-if="loading" class="loading-section">
			<div class="spinner"></div>
			<p>Loading subscription information...</p>
		</section>

		<!-- Current Subscription Info (for Plus subscribers) -->
		<section v-else-if="subscriptionType === 'plus'" class="subscription-info">
			<h2 class="subscription-title">Your Subscription</h2>

			<div class="current-plan">
				<div class="plan-badge plus">PLUS</div>

				<div class="plan-details">
					<h3>GrubVision Plus</h3>
					<p>You are currently subscribed to our plus plan.</p>
					<p v-if="endDate">
						Subscription ends: {{ new Date(endDate).toLocaleDateString() }}
					</p>

					<div v-if="getActiveSubscription()" class="subscription-meta">
						<p class="purchase-date">
							Purchased: {{ formatDate(getActiveSubscription().purchaseTime) }}
						</p>
						<p class="renewal-status">
							{{
								getActiveSubscription().isAutoRenewing
									? "Auto-renews"
									: "Cancelled"
							}}
						</p>
					</div>
				</div>
			</div>

			<!-- Management Link -->
			<div class="action-buttons">
				<button
					v-if="platformActive === 'android'"
					class="manage-button"
					@click="manageActiveSubscription()">
					Manage Subscription
				</button>
				<p v-else-if="platformActive === 'trial'">
					Enjoy the trial period and explore the features.
				</p>
				<p v-else-if="platformActive === 'gifted_feedback'">
					Enjoy the subscription that was gifted to you for being a tester.
				</p>
				<p v-else-if="platformActive === 'gifted_forever'">
					You have a lifelong subscription on the house!
				</p>
				<p
					v-else-if="platformActive !== 'android' && platformActive !== null"
					class="subscription-message">
					Your subscription is managed through
					{{ getPlatformName(platformActive) }}
				</p>
				<p v-else class="subscription-message">
					Your subscription information cannot be managed at this time
				</p>
			</div>
		</section>

		<!-- Free Plan + Available Subscriptions -->
		<section v-else class="subscription-info">
			<h2 class="subscription-title">Your Subscription</h2>

			<!-- Free Plan Banner -->
			<div class="current-plan free-plan">
				<div class="plan-badge basic">FREE</div>

				<div class="plan-details">
					<h3>GrubVision Free Plan</h3>
					<p>
						You are currently using the free plan. Upgrade to Plus for more
						features!
					</p>
				</div>
			</div>

			<h3 class="upgrade-title">Upgrade to plus</h3>

			<!-- Available Plans (optimized for mobile) -->
			<div
				v-if="
					Object.keys(subscriptionDetails).length === 0 && !billingDetailsLoaded
				"
				class="loading-plans">
				<div class="spinner small"></div>
				<p>Loading available plans...</p>
			</div>

			<div v-else class="subscription-offers">
				<!-- For each product/SKU -->
				<div v-for="(details, index) in subscriptionDetails" :key="index">
					<!-- For each offer of this product -->
					<div
						v-for="(offer, offerIdx) in details.subscriptionOfferDetails"
						:key="offerIdx"
						class="product-card">
						<div class="description">
							<!-- Parse the offer to determine if it's monthly or yearly -->
							<h3>
								{{ getPlanTitle(details, offer) }}
							</h3>

							<!-- Price and period -->
							<div
								v-if="offer.pricingPhases && offer.pricingPhases.length > 0"
								class="price-container">
								<h5>
									{{ offer.pricingPhases[0].formattedPrice }} /
									{{ formatPeriod(offer.pricingPhases[0].billingPeriod) }}
								</h5>
								<p class="product-description">
									{{ getPlanDescription(details, offer) }}
								</p>
							</div>
						</div>

						<button
							class="checkout-button"
							@click="subscribe(details.productId, offer.token)"
							:disabled="isOfferProcessing(details.productId, offer.token)">
							<span
								v-if="isOfferProcessing(details.productId, offer.token)"
								class="spinner small"></span>
							Subscribe
						</button>
					</div>
				</div>
			</div>
		</section>
		<!-- <div v-text="tokenStore.getId()"></div> -->
	</div>
</template>

<script setup>
	import { ref, onMounted, watch, onBeforeMount } from "vue"
	import { useBilling } from "@/plugins/Billing/useBilling"
	import { useTokenStore } from "@/stores/token"
	import { fetchWithAuth } from "@/utils/api-client"

	// Subscription SKUs to load
	const SUBSCRIPTION_SKUS = [
		"grubvision.plus.subscription",
		// Add other subscription SKUs if needed
	]

	// Use billing composable
	const {
		isReady,
		isConnecting,
		connectionError,
		subscriptions,
		subscriptionDetails,
		lastError,
		loadSubscriptionDetails,
		loadSubscriptions,
		purchaseSubscription,
		manageSubscription,
		hasActiveSubscription,
	} = useBilling()

	const tokenStore = useTokenStore()
	// tokenStore.loadTokens()

	// Track loading state for each subscription offer
	const processingOffers = ref(new Map())

	// State variables for server subscription data
	const loading = ref(true)
	const error = ref(null)
	const subscriptionType = ref("basic")
	const platformActive = ref(null)
	const billingDetailsLoaded = ref(false)
	const endDate = ref(null)

	// Load data when billing service is ready
	watch(isReady, async (ready) => {
		if (ready) {
			await loadAndroidSubscriptionDetails()
		}
	})

	// Fetch subscription info from server
	const fetchSubscriptionInfo = async () => {
		try {
			loading.value = true
			const response = await fetchWithAuth("/api/get-subscription")

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`)
			}

			const data = await response.json()
			subscriptionType.value = data.subscriptionType
			platformActive.value = data.platformActive
			endDate.value = data.endDate
			return data
		} catch (err) {
			error.value = err.message || "Failed to load subscription information"
			console.error("Error fetching subscription info:", err)
			return null
		} finally {
			loading.value = false
		}
	}

	// Get platform display name
	const getPlatformName = (platform) => {
		switch (platform) {
			case "web":
				return "Web Browser"
			case "ios":
				return "Apple App Store"
			default:
				return platform
		}
	}

	// Load Android-specific subscription details
	const loadAndroidSubscriptionDetails = async () => {
		try {
			// First fetch the server subscription status
			const serverSubData = await fetchSubscriptionInfo()

			// Then load Android billing details if we're still on basic plan or if platformActive is android
			if (
				subscriptionType.value === "basic" ||
				platformActive.value === "android"
			) {
				await loadSubscriptions()
				await loadSubscriptionDetails(SUBSCRIPTION_SKUS)
			}

			billingDetailsLoaded.value = true
		} catch (error) {
			console.error("Failed to load subscription data:", error)
		}
	}

	// Subscribe to a plan
	const subscribe = async (sku, offerId) => {
		// Generate a unique key for this subscription offer
		const offerKey = `${sku}-${offerId}`

		// Set this specific offer as processing
		processingOffers.value.set(offerKey, true)

		try {
			await purchaseSubscription(sku, offerId, "" + tokenStore.getId())
			// After successful purchase, refresh the subscription info
			await fetchSubscriptionInfo()
		} catch (error) {
			console.error("Subscription failed:", error)
		} finally {
			// Clear the processing state for this specific offer
			processingOffers.value.delete(offerKey)
		}
	}

	// Check if a specific offer is being processed
	const isOfferProcessing = (sku, offerId) => {
		const offerKey = `${sku}-${offerId}`
		return processingOffers.value.has(offerKey)
	}

	// Open subscription management for a specific SKU
	// const manage = (sku) => {
	// 	manageSubscription(sku)
	// }

	// Get active subscription object
	const getActiveSubscription = () => {
		const activeSku = Object.keys(subscriptions).find(
			(sku) => subscriptions[sku] && subscriptions[sku].isAutoRenewing
		)

		return activeSku ? subscriptions[activeSku] : null
	}

	// Open subscription management for any active subscription
	const manageActiveSubscription = () => {
		const activeSku = Object.keys(subscriptions).find(
			(sku) => subscriptions[sku] && subscriptions[sku].isAutoRenewing
		)

		if (activeSku) {
			manageSubscription(activeSku)
		}
	}

	// Check if user has any active subscription
	// const hasAnyActiveSubscription = () => {
	// 	return Object.keys(subscriptions).some(
	// 		(sku) => subscriptions[sku] && subscriptions[sku].isAutoRenewing
	// 	)
	// }

	// Get appropriate title based on billing period
	const getPlanTitle = (details, offer) => {
		if (!offer.pricingPhases || !offer.pricingPhases.length) {
			return details.name || "Plus"
		}

		const period = offer.pricingPhases[0].billingPeriod
		if (period.includes("Y")) {
			return "Plus Yearly"
		} else if (period.includes("M")) {
			return "Plus Monthly"
		}

		return details.name || "Plus"
	}

	// Get appropriate description based on billing period
	const getPlanDescription = (details, offer) => {
		if (!offer.pricingPhases || !offer.pricingPhases.length) {
			return details.description || "plus subscription"
		}

		const period = offer.pricingPhases[0].billingPeriod
		if (period.includes("Y")) {
			return "Best value plan with all plus features"
		} else if (period.includes("M")) {
			return "Flexible monthly subscription"
		}

		return details.description || "plus subscription"
	}

	// Format date from milliseconds timestamp
	const formatDate = (timestamp) => {
		if (!timestamp) return "Unknown"
		const date = new Date(timestamp)
		return date.toLocaleDateString()
	}

	// Format subscription period for display
	const formatPeriod = (period) => {
		if (!period) return ""

		// Period format is like P1M (1 month), P1Y (1 year), etc.
		const match = period.match(/P(\d+)([DWMY])/)
		if (!match) return period

		const [_, count, unit] = match

		const unitMap = {
			D: "day",
			W: "week",
			M: "month",
			Y: "year",
		}

		const unitName = unitMap[unit] || unit
		return `${count} ${unitName}${Number(count) > 1 ? "s" : ""}`
	}

	// Load data on component mount if ready
	onMounted(() => {
		if (isReady.value) {
			loadAndroidSubscriptionDetails()
		}
	})

	// onBeforeMount(async () => {
	// 	await tokenStore.loadTokens()
	// })
</script>

<style lang="less" scoped>
	.subscription-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		font-family: -apple-system, BlinkMacSystemFont, sans-serif;
		max-width: 100%;
		margin: 0 auto;
	}

	.subscription-title {
		text-align: center;
		margin-bottom: 1.5rem;
		color: #32325d;
		font-size: 1.5rem;
	}

	.upgrade-title {
		text-align: center;
		margin: 1.5rem 0;
		color: #32325d;
		font-size: 1.2rem;
	}

	section {
		background: #ffffff;
		display: flex;
		flex-direction: column;
		width: 100%;
		border-radius: 6px;
		padding: 1.5rem;
		box-shadow: 0 0.3rem 1rem rgba(0, 0, 0, 0.1);
		margin-bottom: 1rem;
	}

	.error-section {
		padding: 0;
		box-shadow: none;
		background: transparent;
		margin-bottom: 0.5rem;

		.error-container {
			padding: 1rem;
			border: 1px solid #ffcdd2;
			border-radius: 8px;
			background-color: #ffebee;
		}

		.error-title {
			font-weight: 600;
			margin-bottom: 0.5rem;
			color: #d32f2f;
			font-size: 1rem;
		}

		.error-message {
			margin-bottom: 4px;
			color: #d32f2f;
			font-size: 0.875rem;
		}
	}

	.loading-section {
		align-items: center;
		justify-content: center;
		text-align: center;
		min-height: 150px;
	}

	.loading-plans {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		text-align: center;
		color: #666;
	}

	.spinner {
		border: 4px solid rgba(0, 0, 0, 0.1);
		width: 36px;
		height: 36px;
		border-radius: 50%;
		border-left-color: #5469d4;
		animation: spin 1s linear infinite;
		margin-bottom: 1rem;
	}

	.spinner.small {
		width: 16px;
		height: 16px;
		margin-right: 8px;
		border-width: 2px;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.current-plan {
		display: flex;
		align-items: flex-start;
		padding: 1.5rem;
		border: 1px solid #e6e6e6;
		border-radius: 8px;
		margin-bottom: 1rem;
	}

	.free-plan {
		margin-bottom: 0.5rem;
	}

	.subscription-meta {
		margin-top: 0.75rem;
		font-size: 0.875rem;
		color: #666;

		p {
			margin: 0.25rem 0;
		}
	}

	.plan-badge {
		padding: 0.5rem 0.75rem;
		border-radius: 4px;
		font-weight: bold;
		font-size: 0.875rem;
		margin-right: 1rem;
		min-width: 60px;
		text-align: center;

		&.plus {
			background-color: #6772e5;
			color: white;
		}

		&.basic {
			background-color: #b7b7b7;
			color: white;
		}
	}

	.plan-details {
		flex-grow: 1;

		h3 {
			margin: 0 0 0.5rem 0;
			font-size: 1.1rem;
		}

		p {
			margin: 0;
			font-size: 0.9rem;
		}
	}

	.action-buttons {
		margin-top: 1rem;
		display: flex;
		justify-content: center;
	}

	.manage-button {
		background: #5469d4;
		color: #ffffff;
		border: 0;
		border-radius: 4px;
		padding: 12px 24px;
		font-size: 1rem;
		font-weight: 600;
		cursor: pointer;
		transition: background 0.2s ease;
		text-decoration: none;
		display: inline-block;
		width: auto;

		&:hover {
			background: #4559c9;
		}
	}

	.subscription-offers {
		display: flex;
		flex-direction: column;
		width: 100%;
	}

	.product-card {
		display: flex;
		flex-direction: column;
		padding: 1.25rem;
		margin-bottom: 1rem;
		border: 1px solid #e6e6e6;
		border-radius: 8px;
		transition: all 0.3s ease;
		position: relative;
	}

	.product-card:hover {
		box-shadow: 0 0.3rem 0.7rem rgba(0, 0, 0, 0.1);
	}

	.product-description {
		margin: 0.5rem 0;
		color: #525f7f;
		font-size: 0.9rem;
	}

	.description {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		margin-bottom: 1rem;
	}

	.price-container {
		margin-top: 0.5rem;
	}

	h3 {
		margin: 0 0 0.25rem 0;
		font-size: 1.2rem;
		color: #32325d;
	}

	h5 {
		margin: 0;
		font-size: 1rem;
		font-weight: 700;
		color: #6772e5;
	}

	button {
		background: #5469d4;
		color: #ffffff;
		border: 0;
		border-radius: 4px;
		padding: 10px 16px;
		font-size: 1rem;
		font-weight: 600;
		cursor: pointer;
		transition: background 0.2s ease;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;

		&:disabled {
			background-color: #a5b4e5;
			cursor: not-allowed;
		}
	}

	button:hover {
		background: #4559c9;
	}

	.checkout-button {
		align-self: flex-end;
	}

	.subscription-message {
		text-align: center;
		margin-top: 1rem;
		color: #666;
		font-size: 0.875rem;
	}
</style>
